import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { APP_NAME } from "../../helpers/constants";

const Terms = () => {
  return (
    <Page>
      <MainHeader>Terms of Use</MainHeader>
      <EffectiveDate>Last updated: July 11, 2026</EffectiveDate>

      <Section>
        <Paragraph>
          These Terms of Use (&quot;Terms&quot;) govern access to and use of the {APP_NAME}{" "}
          platform, including our website, intake workflows, Business Personal Property (BPP) tax
          audit engine, appeal-preparation tools, and related services (&quot;Services&quot;). The
          Services are offered by Junoware LLC doing business as {APP_NAME} (&quot;{APP_NAME},&quot;
          &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
        </Paragraph>
        <Paragraph>
          By accessing the Services, submitting a waitlist application, uploading documents,
          requesting an audit, or executing a contingency engagement, you agree to these Terms and
          our <Link to="/privacy">Privacy Policy</Link>.
        </Paragraph>
      </Section>

      <Section>
        <H2>1. Description of Service and Scope</H2>
        <H3>1.1 B2B Limitation</H3>
        <Paragraph>
          The Services are provided strictly for commercial entities and their authorized corporate
          representatives. {APP_NAME} is a business-to-business platform. It is not intended for
          consumers, individuals acting in a personal capacity, or residential real estate matters.
          You represent that you are at least 18 years of age, acting on behalf of a business
          entity, and have authority to bind that entity to these Terms.
        </Paragraph>
        <H3>1.2 Tax Auditing and Appeals</H3>
        <Paragraph>
          {APP_NAME} provides an analytical diagnostic platform that reviews Business Personal
          Property (BPP) assessment notices, corporate asset ledgers, and depreciation schedules to
          identify &quot;phantom assets,&quot; misclassified useful lives, and over-assessments on
          machinery, equipment, tools, commercial furniture, server arrays, and similar depreciable
          personal property.
        </Paragraph>
        <Paragraph>
          The Services do not evaluate, assess, or opine on real property or structural land
          valuations, residential homestead assessments, income tax, payroll tax, or sales tax
          obligations. Outputs are computational aids for BPP diagnostics and appeal preparation and
          are subject to your review before any filing.
        </Paragraph>
      </Section>

      <Section>
        <H2>2. Contingency and Performance-Based Fees</H2>
        <H3>2.1 Fee Structure</H3>
        <Paragraph>
          {APP_NAME} operates on a contingency model. Where a separate contingency engagement or
          order form applies, the service fee is calculated as exactly{" "}
          <strong>35% of the realized and verified tax-bill savings</strong> attributable to the BPP
          valuation reduction secured through the audit and related appeal process. No upfront
          retainer is required to run an initial diagnostic review.
        </Paragraph>
        <H3>2.2 Calculation Standards</H3>
        <Paragraph>
          Savings are calculated by comparing the original county assessment against the adjusted
          valuation secured through the audit, using the applicable local statutory assessment
          frameworks. For example, in Missouri this may include the standard 33.33% statutory
          assessment ratio applied against historical cost and relevant percent-good or useful-life
          schedules. Fee calculations will reference the verified assessed-value delta and
          applicable effective tax rate.
        </Paragraph>
        <H3>2.3 Payment Terms</H3>
        <Paragraph>
          Fees are due only after the county assessor (or other competent local tax authority)
          officially approves the valuation reduction and the resulting tax-bill savings are
          verified. If no verified reduction is achieved, you owe {APP_NAME} $0 under the
          contingency model for that engagement, unless a separate written agreement expressly
          states otherwise.
        </Paragraph>
        <Paragraph>
          Marketing summaries on the website are informational. If fee language in a signed
          contingency engagement conflicts with website copy, the signed engagement controls.
        </Paragraph>
      </Section>

      <Section>
        <H2>3. Authority to Represent and Appeal Filing</H2>
        <H3>3.1 Limited Authorization</H3>
        <Paragraph>
          By submitting a signed contingency agreement, you grant {APP_NAME} and its designated
          affiliates limited authority and agency to represent the business entity before municipal
          and county assessor boards (and equivalent local tax authorities) solely to prepare, file,
          and support BPP tax appeals and related valuation correction requests arising from the
          engagement.
        </Paragraph>
        <Paragraph>
          This limited agency does not make {APP_NAME} your attorney, CPA, enrolled agent, or
          general legal counsel. Scope is confined to the BPP appeal and documentation workflow
          described in the contingency agreement. Unless that agreement is in place, final
          execution, certification, and submission of any appeal remain your responsibility.
        </Paragraph>
        <H3>3.2 User Responsibilities</H3>
        <Paragraph>
          You must provide accurate, complete, and current corporate asset listings, assessment
          notices, and supporting records. You represent that you are authorized to share all
          uploaded materials with {APP_NAME}. {APP_NAME} is not liable for errors, penalties,
          interest, or adverse outcomes resulting from falsified, incomplete, outdated, or
          inaccurate asset records you supply.
        </Paragraph>
      </Section>

      <Section>
        <H2>4. Data License and Submission Representation</H2>
        <Paragraph>
          By uploading any document (including county BPP notices and equipment ledgers), you grant{" "}
          {APP_NAME} a non-exclusive, royalty-free license to host, parse, analyze, and use the data
          as needed to perform diagnostics, prepare appeal packages, and operate the Services, as
          further described in our <Link to="/privacy">Privacy Policy</Link>.
        </Paragraph>
      </Section>

      <Section>
        <H2>5. Acceptable Use</H2>
        <List>
          <li>Use the Services only for lawful B2B BPP tax analysis and appeal purposes.</li>
          <li>Do not attempt to disrupt, reverse engineer, scrape, or misuse the platform.</li>
          <li>Do not submit fraudulent, falsified, or misleading documentation.</li>
          <li>Comply with all applicable federal, state, and local laws.</li>
        </List>
      </Section>

      <Section>
        <H2>6. Disclaimers and Limitation of Liability</H2>
        <Paragraph>
          <strong>
            The Services are provided &quot;as is&quot; and &quot;as available.&quot; {APP_NAME}{" "}
            does not guarantee tax reductions or official government outcomes. County processing
            times, hearing results, and assessment methodologies are outside our control.
          </strong>
        </Paragraph>
        <Paragraph>
          To the fullest extent permitted by law, {APP_NAME} disclaims warranties of
          merchantability, fitness for a particular purpose, and non-infringement. Except for fees
          owed under a signed contingency engagement for verified savings, {APP_NAME}&apos;s total
          liability arising from the Services shall not exceed the greater of (a) fees actually paid
          by you to {APP_NAME} in the twelve (12) months preceding the claim, or (b) one hundred
          dollars ($100). {APP_NAME} is not liable for indirect, incidental, special, consequential,
          or punitive damages, including lost profits or tax-authority penalties arising from
          inaccurate submissions.
        </Paragraph>
      </Section>

      <Section>
        <H2>7. Indemnification</H2>
        <Paragraph>
          You agree to indemnify and hold harmless {APP_NAME} and its officers, directors,
          employees, and affiliates from claims, damages, and expenses (including reasonable
          attorneys&apos; fees) arising from your use of the Services, your submitted data, filings
          you authorize, or your violation of these Terms.
        </Paragraph>
      </Section>

      <Section>
        <H2>8. Governing Law</H2>
        <Paragraph>
          These Terms are governed by the laws of the State of Missouri, without regard to
          conflict-of-law principles. Disputes shall be resolved in St. Louis County, Missouri,
          unless otherwise required by applicable law.
        </Paragraph>
      </Section>

      <Section>
        <H2>9. Contact</H2>
        <Paragraph>
          Questions regarding these Terms may be directed through our{" "}
          <Link to="/contact">Contact</Link> page or support@solvtax.com.
        </Paragraph>
        <Paragraph>
          See also our <Link to="/privacy">Privacy Policy</Link> for how we handle corporate data
          submitted through the platform.
        </Paragraph>
      </Section>
    </Page>
  );
};

const Page = styled.div`
  color: #0f172a;
  font-size: 0.9375rem;
  line-height: 1.65;
  text-align: left;
  width: min(56rem, 92%);
  max-width: 56rem;
  margin: 3rem auto 2.5rem;
  padding-bottom: 3rem;

  a {
    color: #0d9488;
    text-decoration: underline;
  }
`;

const MainHeader = styled.h1`
  text-align: center;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: #0f172a;
`;

const EffectiveDate = styled.p`
  text-align: center;
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0.75rem 0 2rem;
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
`;

const H2 = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
  color: #0f172a;
  text-align: left;
`;

const H3 = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem;
  color: #0f172a;
  text-align: left;
`;

const Paragraph = styled.p`
  margin: 0.75rem 0;
  color: #334155;
  text-align: left;
`;

const List = styled.ul`
  margin: 0.75rem 0 0.75rem 1.25rem;
  padding-left: 1.25rem;
  color: #334155;
  text-align: left;
  list-style-position: outside;

  li + li {
    margin-top: 0.4rem;
  }
`;

export default Terms;
